import {AuthSessionApiService} from '@npck/auth-frontend-api';
import {rxSpinner, SentryService} from '@npck/libs/commons';
import {Subject} from 'rxjs';
import FaceTecLocalizationStringKK from '../../../../../assets/i18n/zoom-strings-kk.json';
import FaceTecLocalizationStringRU from '../../../../../assets/i18n/zoom-strings-ru.json';
import {AuthHolderService} from '../../../auth-holder.service';
import {LivenessService} from '../../../liveness.service';
import {
    FACE_TEC_SDK_IMAGES,
    FACE_TEC_SDK_RESOURCES,
    facetecCustomization,
    faceTecDynamicDimmingCustomization,
    faceTecLowLightCustomization,
    FaceTecResult,
    FaceTecZoomState,
} from '../facetec-customization';
import {LivenessCheckProcessor} from './LivenessCheckProcessor';

interface NpckFaceTecAppAPI {
    initSdk: () => void;
    initLiveness: (
        authSessionApiService: AuthSessionApiService,
        authHolderService: AuthHolderService,
        sentryService: SentryService,
        livenessService: LivenessService,
        currentLocale: string,
        isMobile: boolean,
        isCancelled: () => boolean,
    ) => Subject<FaceTecResult>;
}

export const NpckFaceTecApp = ((): NpckFaceTecAppAPI => {
    let sdkInitStarted = false;
    let sdkInitialized = false;
    const faceTecStateSubject = new Subject<FaceTecResult>();

    function initSdk(): void {
        if (sdkInitStarted) {
            return;
        }
        sdkInitStarted = true;
        const appConfig = window.appConfig;
        const cdnEnabled = ['stage'].includes(appConfig.env);
        const baseUrlWithCdnIfEnabled = cdnEnabled ? `https://cf-${window.location.host}/` : '';
        FaceTecSDK.setResourceDirectory(baseUrlWithCdnIfEnabled + FACE_TEC_SDK_RESOURCES);
        FaceTecSDK.setImagesDirectory(baseUrlWithCdnIfEnabled + FACE_TEC_SDK_IMAGES);
        FaceTecSDK.setMaxAuditTrailImages(FaceTecSDK.FaceTecAuditTrailImagesToReturn.UP_TO_SIX);
        FaceTecSDK.initializeInProductionMode(
            appConfig.zoomBrowserProductionKey,
            appConfig.zoomDeviceKeyIdentifier,
            appConfig.zoomPublicEncryptionKey,
            sdkInitCallback,
        );
    }

    function initLiveness(
        authSessionApiService: AuthSessionApiService,
        authHolderService: AuthHolderService,
        sentryService: SentryService,
        livenessService: LivenessService,
        currentLocale: string,
        isMobile: boolean,
        isCancelled: () => boolean,
    ): Subject<FaceTecResult> {
        if (isCancelled()) {
            return faceTecStateSubject;
        }
        sentryService.addTag('facetecServiceStarted', true);
        sentryService.addBreadcrumb({message: 'FacetecService init started'});
        if (sdkInitialized && FaceTecSDK.getStatus() === FaceTecSDK.FaceTecSDKStatus.Initialized) {
            customizeFaceTec(currentLocale);
            localize(authHolderService.iin, currentLocale);
            authSessionApiService.initLiveness()
                .pipe(rxSpinner('livenessStarted'))
                .subscribe({
                    next: response => {
                        if (isCancelled()) {
                            return;
                        }
                        new LivenessCheckProcessor(
                            response.sessionToken,
                            authSessionApiService,
                            authHolderService,
                            sentryService,
                            livenessService,
                            faceTecStateSubject,
                            isMobile,
                        );
                    },
                });
        } else {
            switch (FaceTecSDK.getStatus()) {
                case FaceTecSDK.FaceTecSDKStatus.ResourcesCouldNotBeLoadedOnLastInit:
                    emitSdkStatus(FaceTecZoomState.COULD_NOT_BE_LOADED);
                    break;
                case FaceTecSDK.FaceTecSDKStatus.NetworkIssues:
                    emitSdkStatus(FaceTecZoomState.NETWORK_ERROR, 'checkLiveness NetworkIssues');
                    break;
                case FaceTecSDK.FaceTecSDKStatus.DeviceInLandscapeMode:
                case FaceTecSDK.FaceTecSDKStatus.DeviceLockedOut:
                    emitSdkStatus(FaceTecZoomState.ORIENTATION_ERROR, 'checkLiveness orientation');
                    break;
                case FaceTecSDK.FaceTecSDKStatus.NeverInitialized:
                case FaceTecSDK.FaceTecSDKStatus.StillLoadingResources:
                    setTimeout(() => initLiveness(authSessionApiService, authHolderService,
                        sentryService, livenessService, currentLocale, isMobile, isCancelled), 200);
                    break;
                case FaceTecSDK.FaceTecSDKStatus.DeviceNotSupported:
                    emitSdkStatus(FaceTecZoomState.DEVICE_NOT_SUPPORTED, 'checkLiveness DeviceNotSupported');
                    break;
                default:
                    emitSdkStatus(FaceTecZoomState.UNKNOWN);
                    break;
            }
        }

        return faceTecStateSubject;
    }

    function customizeFaceTec(currentLocale: string): void {
        FaceTecSDK.setCustomization(facetecCustomization(currentLocale));
        FaceTecSDK.setLowLightCustomization(faceTecLowLightCustomization(currentLocale));
        FaceTecSDK.setDynamicDimmingCustomization(faceTecDynamicDimmingCustomization(currentLocale));
    }

    function sdkInitCallback(): void {
        const status = FaceTecSDK.getStatus();
        if (
            status === FaceTecSDK.FaceTecSDKStatus.Initialized ||
            status === FaceTecSDK.FaceTecSDKStatus.DeviceInLandscapeMode ||
            status === FaceTecSDK.FaceTecSDKStatus.DeviceLockedOut
        ) {
            sdkInitialized = true;
        }
    }

    function emitSdkStatus(state: FaceTecZoomState, errorSource?: string): void {
        const rootStatus = FaceTecSDK.FaceTecSDKStatus[FaceTecSDK.getStatus()];
        setTimeout(() => faceTecStateSubject.next({status: state, rootStatus: rootStatus, errorSource: errorSource}));
    }

    function localize(iin: string, currentLocale: string): void {
        let localizationString: any;
        switch (currentLocale) {
            case 'ru':
                localizationString = FaceTecLocalizationStringRU;
                break;
            case 'kk':
                localizationString = FaceTecLocalizationStringKK;
                break;
        }
        localizationString = Object.assign(localizationString, {
            FaceTec_instructions_message_ready_desktop:
                localizationString.FaceTec_instructions_message_ready_desktop.replace('{{iin}}', iin),
            FaceTec_instructions_message_ready_2_mobile: iin,
        });
        FaceTecSDK.configureLocalization(localizationString);
    }

    return {
        initSdk,
        initLiveness,
    };
})();
